/**
 * HIPAA-Compliant Audit Service
 * Tracks all patient data access and modifications for compliance
 */
import { 
  collection, 
  addDoc, 
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs
} from 'firebase/firestore';
import { auth, db } from '../config/firebase.config';

// Audit action types
export const AUDIT_ACTIONS = {
  // Authentication
  LOGIN: 'USER_LOGIN',
  LOGOUT: 'USER_LOGOUT',
  SESSION_TIMEOUT: 'SESSION_TIMEOUT',
  
  // Patient data access
  VIEW_PATIENT: 'VIEW_PATIENT',
  VIEW_PATIENT_LIST: 'VIEW_PATIENT_LIST',
  SEARCH_PATIENTS: 'SEARCH_PATIENTS',
  
  // Bed management
  VIEW_BED: 'VIEW_BED',
  ASSIGN_BED: 'ASSIGN_BED',
  RELEASE_BED: 'RELEASE_BED',
  TRANSFER_BED: 'TRANSFER_BED',
  
  // Clinical actions
  UPDATE_ESI: 'UPDATE_ESI',
  ADD_CLINICAL_NOTE: 'ADD_CLINICAL_NOTE',
  VIEW_CLINICAL_DATA: 'VIEW_CLINICAL_DATA',
  
  // Emergency access
  EMERGENCY_ACCESS: 'EMERGENCY_ACCESS_REQUESTED'
};

// Helper to get user's IP address (for audit trail)
const getUserIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    return 'unknown';
  }
};

// Get browser fingerprint for device tracking
const getBrowserFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('fingerprint', 2, 2);
  return canvas.toDataURL().slice(-50);
};

/**
 * Create an audit log entry
 * @param {string} action - The action being performed (from AUDIT_ACTIONS)
 * @param {string} resource - The resource being accessed (e.g., 'patient/123', 'bed/A1')
 * @param {object} details - Additional details about the action
 */
export const createAuditLog = async (action, resource, details = {}) => {
  try {
    if (!auth.currentUser) {
      console.error('No authenticated user for audit log');
      return;
    }

    const auditEntry = {
      // Required fields for security rules
      timestamp: serverTimestamp(),
      userId: auth.currentUser.uid,
      action,
      resource,
      
      // Additional metadata
      userEmail: auth.currentUser.email,
      userRole: auth.currentUser.customClaims?.role || 'unknown',
      ipAddress: await getUserIP(),
      userAgent: navigator.userAgent,
      deviceFingerprint: getBrowserFingerprint(),
      sessionId: sessionStorage.getItem('sessionId') || 'unknown',
      
      // Action-specific details
      details: {
        ...details,
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    };

    await addDoc(collection(db, 'audit'), auditEntry);
    console.log('Audit log created:', action, resource);
  } catch (error) {
    // Audit logging should never break the app
    console.error('Failed to create audit log:', error);
    
    // Try to at least log to console for debugging
    console.log('AUDIT_FALLBACK:', {
      action,
      resource,
      details,
      error: error.message
    });
  }
};

/**
 * Create a patient access log (specific for HIPAA)
 * @param {string} patientId - The patient ID being accessed
 * @param {string} accessType - Type of access (view, edit, etc.)
 * @param {string} reason - Reason for access
 */
export const logPatientAccess = async (patientId, accessType, reason = 'routine_care') => {
  try {
    const accessLog = {
      timestamp: serverTimestamp(),
      userId: auth.currentUser.uid,
      patientId,
      accessType,
      reason,
      userEmail: auth.currentUser.email,
      userRole: auth.currentUser.customClaims?.role || 'unknown',
      ipAddress: await getUserIP(),
      sessionId: sessionStorage.getItem('sessionId') || 'unknown'
    };

    await addDoc(collection(db, 'accessLogs'), accessLog);
    
    // Also create general audit log
    await createAuditLog(
      AUDIT_ACTIONS.VIEW_PATIENT,
      `patient/${patientId}`,
      { accessType, reason }
    );
  } catch (error) {
    console.error('Failed to log patient access:', error);
  }
};

/**
 * Log emergency access request (break glass)
 * @param {string} resource - Resource being accessed
 * @param {string} reason - Detailed reason for emergency access
 */
export const logEmergencyAccess = async (resource, reason) => {
  try {
    const emergencyLog = {
      timestamp: serverTimestamp(),
      requestedBy: auth.currentUser.uid,
      reason,
      resource,
      userEmail: auth.currentUser.email,
      userRole: auth.currentUser.customClaims?.role || 'unknown',
      ipAddress: await getUserIP(),
      approved: false // Requires admin approval
    };

    await addDoc(collection(db, 'emergencyAccess'), emergencyLog);
    
    // Also create audit log
    await createAuditLog(
      AUDIT_ACTIONS.EMERGENCY_ACCESS,
      resource,
      { reason, emergencyAccess: true }
    );
    
    return true;
  } catch (error) {
    console.error('Failed to log emergency access:', error);
    return false;
  }
};

/**
 * Get recent audit logs for current user (for user activity dashboard)
 * @param {number} limitCount - Number of logs to retrieve
 */
export const getUserAuditLogs = async (limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'audit'),
      where('userId', '==', auth.currentUser.uid),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Failed to get user audit logs:', error);
    return [];
  }
};

/**
 * Audit decorator for wrapping functions that access patient data
 * @param {string} action - The audit action type
 * @param {function} getResource - Function to get resource from arguments
 */
export const withAudit = (action, getResource) => {
  return (target, propertyKey, descriptor) => {
    const originalMethod = descriptor.value;
    
    descriptor.value = async function(...args) {
      const resource = getResource(...args);
      
      try {
        // Log the action
        await createAuditLog(action, resource, {
          method: propertyKey,
          arguments: args.length
        });
        
        // Execute the original method
        const result = await originalMethod.apply(this, args);
        
        return result;
      } catch (error) {
        // Log the error in audit
        await createAuditLog(action, resource, {
          method: propertyKey,
          error: error.message,
          failed: true
        });
        
        throw error;
      }
    };
    
    return descriptor;
  };
};

// Export audit service
export default {
  createAuditLog,
  logPatientAccess,
  logEmergencyAccess,
  getUserAuditLogs,
  withAudit,
  AUDIT_ACTIONS
}; 